import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { useLeadTimeContext } from '../context/LeadTimeContext';
import { TrackingData, VisitorStatistics } from '../types';
import { useForm } from 'react-hook-form';
// @ts-ignore - TypeScript may have module resolution issues with subpath exports
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import '../styles/LeadForm.css';

type LeadFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  projectType: string;
  budget: number;
  timeline: string;
  location: string;
  source: 'web-form' | 'facebook-form' | 'instagram-form' | 'ebook' | 'instagram-follow' | 'facebook-follow' | 'b2b-visit' | 'cold-call' | 'call-in' | 'referral' | 'unspecified';
  notes?: string;
};

const leadSchema: yup.ObjectSchema<LeadFormData> = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().optional(),
  company: yup.string().optional(),
  projectType: yup.string().required('Project type is required'),
  budget: yup.number().min(0, 'Budget must be positive').required('Budget is required'),
  timeline: yup.string().required('Timeline is required'),
  location: yup.string().required('Location is required'),
  source: yup.string().oneOf(['web-form', 'facebook-form', 'instagram-form', 'ebook', 'instagram-follow', 'facebook-follow', 'b2b-visit', 'cold-call', 'call-in', 'referral', 'unspecified'] as const).required('Source is required'),
  notes: yup.string().optional(),
});

const LeadForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { createLead, updateLead, leads } = useCRM();
  const { user } = useAuth();
  const { setActiveLead } = useLeadTimeContext();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LeadFormData>({
    resolver: yupResolver(leadSchema),
  });

  const [showTrackingData, setShowTrackingData] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingData>({});
  const [statistics, setStatistics] = useState<VisitorStatistics>({});
  const [metricsLiteId, setMetricsLiteId] = useState('');

  // Time tracking: set active lead when editing
  useEffect(() => {
    if (isEditing && id) {
      setActiveLead(id);
    } else {
      setActiveLead(null);
    }
    return () => setActiveLead(null);
  }, [isEditing, id, setActiveLead]);

  // Load existing lead data if editing
  React.useEffect(() => {
    if (isEditing && id) {
      const existingLead = leads.find(l => l.id === id);
      if (existingLead) {
        setValue('firstName', existingLead.firstName);
        setValue('lastName', existingLead.lastName);
        setValue('email', existingLead.email);
        setValue('phone', existingLead.phone || undefined);
        setValue('company', existingLead.company || undefined);
        setValue('projectType', existingLead.projectType);
        setValue('budget', existingLead.budget);
        setValue('timeline', existingLead.timeline);
        setValue('location', existingLead.location);
        setValue('source', existingLead.source);
        setValue('notes', existingLead.notes || undefined);
        
        // Load tracking data if exists
        if (existingLead.trackingData || existingLead.statistics) {
          setShowTrackingData(true);
          setTrackingData(existingLead.trackingData || {});
          setStatistics(existingLead.statistics || {});
          setMetricsLiteId(existingLead.metricsLiteId || '');
        }
      }
    }
  }, [id, isEditing, leads, setValue]);

  const onSubmit = async (data: LeadFormData) => {
    setLoading(true);
    try {
      const leadData: any = {
        ...data,
        status: isEditing ? (leads.find(l => l.id === id)?.status || 'new') : 'new',
      };

      // Add tracking data if provided
      if (showTrackingData && (Object.keys(trackingData).length > 0 || Object.keys(statistics).length > 0)) {
        leadData.metricsLiteId = metricsLiteId || undefined;
        leadData.trackingData = Object.keys(trackingData).length > 0 ? trackingData : undefined;
        leadData.statistics = Object.keys(statistics).length > 0 ? statistics : undefined;
      }

      if (isEditing && id) {
        await updateLead(id, leadData);
      } else {
        await createLead({
          ...leadData,
          assignedTo: user?.id,
          addedBy: user?.id, // Track who added the lead
        });
      }
      navigate('/crm');
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Error saving lead. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lead-form-container">
      <header className="form-header">
        <div className="header-content">
          <button onClick={() => navigate('/crm')} className="back-button">
            ← Back to CRM
          </button>
          <h1>{isEditing ? 'Edit Lead' : 'Create New Lead'}</h1>
        </div>
      </header>

      <main className="form-content">
        <form onSubmit={handleSubmit(onSubmit)} className="lead-form">
          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  {...register('firstName')}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-message">{errors.firstName.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  {...register('lastName')}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-message">{errors.lastName.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  {...register('email')}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  {...register('phone')}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  {...register('company')}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Project Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="projectType">Project Type *</label>
                <select
                  id="projectType"
                  {...register('projectType')}
                  className={errors.projectType ? 'error' : ''}
                >
                  <option value="">Select project type</option>
                  <option value="timber-frame-home">Timber Frame Home</option>
                  <option value="addition">Addition</option>
                  <option value="renovation">Renovation</option>
                  <option value="barn">Barn/Outbuilding</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select>
                {errors.projectType && <span className="error-message">{errors.projectType.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="budget">Budget *</label>
                <input
                  type="number"
                  id="budget"
                  {...register('budget')}
                  className={errors.budget ? 'error' : ''}
                  placeholder="Enter budget amount"
                />
                {errors.budget && <span className="error-message">{errors.budget.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="timeline">Timeline *</label>
                <select
                  id="timeline"
                  {...register('timeline')}
                  className={errors.timeline ? 'error' : ''}
                >
                  <option value="">Select timeline</option>
                  <option value="asap">ASAP</option>
                  <option value="1-3-months">1-3 Months</option>
                  <option value="3-6-months">3-6 Months</option>
                  <option value="6-12-months">6-12 Months</option>
                  <option value="1-year-plus">1 Year+</option>
                  <option value="flexible">Flexible</option>
                </select>
                {errors.timeline && <span className="error-message">{errors.timeline.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="location">Location *</label>
                <input
                  type="text"
                  id="location"
                  {...register('location')}
                  className={errors.location ? 'error' : ''}
                  placeholder="City, State or Address"
                />
                {errors.location && <span className="error-message">{errors.location.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="source">Lead Source *</label>
                <select
                  id="source"
                  {...register('source')}
                  className={errors.source ? 'error' : ''}
                >
                  <option value="">Select source</option>
                  <option value="unspecified">Unspecified</option>
                  <option value="web-form">Web Form</option>
                  <option value="facebook-form">Facebook Form</option>
                  <option value="instagram-form">Instagram Form</option>
                  <option value="ebook">Ebook</option>
                  <option value="instagram-follow">Instagram Follow</option>
                  <option value="facebook-follow">Facebook Follow</option>
                  <option value="b2b-visit">B2B Visit</option>
                  <option value="cold-call">Cold Call</option>
                  <option value="call-in">Call-in</option>
                  <option value="referral">Referral</option>
                </select>
                {errors.source && <span className="error-message">{errors.source.message}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Additional Information</h2>
            <div className="form-group full-width">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                {...register('notes')}
                rows={4}
                placeholder="Any additional information about this lead..."
              />
            </div>
          </div>

          {/* Tracking Data Section */}
          <div className="form-section">
            <div className="section-header-toggle">
              <h2>Web Tracking Data (Optional)</h2>
              <button
                type="button"
                onClick={() => setShowTrackingData(!showTrackingData)}
                className="toggle-button"
              >
                {showTrackingData ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showTrackingData && (
              <div className="tracking-form-section">
                <div className="form-group">
                  <label htmlFor="metricsLiteId">Metrics Lite Visitor ID</label>
                  <input
                    type="text"
                    id="metricsLiteId"
                    value={metricsLiteId}
                    onChange={(e) => setMetricsLiteId(e.target.value)}
                    placeholder="Enter visitor ID"
                  />
                </div>

                <div className="form-subsection">
                  <h3>Tracking Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="ipAddress">IP Address</label>
                      <input
                        type="text"
                        id="ipAddress"
                        value={trackingData.ipAddress || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, ipAddress: e.target.value })}
                        placeholder="192.168.1.1"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="userAgent">User Agent</label>
                      <input
                        type="text"
                        id="userAgent"
                        value={trackingData.userAgent || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, userAgent: e.target.value })}
                        placeholder="Mozilla/5.0..."
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="screenWidth">Screen Width</label>
                      <input
                        type="number"
                        id="screenWidth"
                        value={trackingData.screenWidth || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, screenWidth: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="1920"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="screenHeight">Screen Height</label>
                      <input
                        type="number"
                        id="screenHeight"
                        value={trackingData.screenHeight || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, screenHeight: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="1080"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="language">Language</label>
                      <input
                        type="text"
                        id="language"
                        value={trackingData.language || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, language: e.target.value })}
                        placeholder="en-US"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="timezone">Timezone</label>
                      <input
                        type="text"
                        id="timezone"
                        value={trackingData.timezone || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, timezone: e.target.value })}
                        placeholder="America/New_York"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-subsection">
                  <h3>Campaign Source (UTM)</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="utmSource">UTM Source</label>
                      <input
                        type="text"
                        id="utmSource"
                        value={trackingData.utmSource || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, utmSource: e.target.value })}
                        placeholder="google"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="utmMedium">UTM Medium</label>
                      <input
                        type="text"
                        id="utmMedium"
                        value={trackingData.utmMedium || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, utmMedium: e.target.value })}
                        placeholder="cpc"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="utmCampaign">UTM Campaign</label>
                      <input
                        type="text"
                        id="utmCampaign"
                        value={trackingData.utmCampaign || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, utmCampaign: e.target.value })}
                        placeholder="summer_sale"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="referrer">Referrer</label>
                      <input
                        type="url"
                        id="referrer"
                        value={trackingData.referrer || ''}
                        onChange={(e) => setTrackingData({ ...trackingData, referrer: e.target.value })}
                        placeholder="https://google.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-subsection">
                  <h3>Statistics</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="totalEvents">Total Events</label>
                      <input
                        type="number"
                        id="totalEvents"
                        value={statistics.totalEvents || ''}
                        onChange={(e) => setStatistics({ ...statistics, totalEvents: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="pageviews">Pageviews</label>
                      <input
                        type="number"
                        id="pageviews"
                        value={statistics.pageviews || ''}
                        onChange={(e) => setStatistics({ ...statistics, pageviews: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="goalCompletions">Goal Completions</label>
                      <input
                        type="number"
                        id="goalCompletions"
                        value={statistics.goalCompletions || ''}
                        onChange={(e) => setStatistics({ ...statistics, goalCompletions: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="totalRevenue">Total Revenue</label>
                      <input
                        type="number"
                        step="0.01"
                        id="totalRevenue"
                        value={statistics.totalRevenue || ''}
                        onChange={(e) => setStatistics({ ...statistics, totalRevenue: e.target.value ? parseFloat(e.target.value) : undefined })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/crm')}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Lead' : 'Create Lead')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default LeadForm;